import { FC } from 'react';
import { NumberUtils, Tr } from '@utils';
import { useLoggedinUser } from '@hooks';
import { Permission, RoleWithAssignees } from '@services';
import { Grid, IconButton, Stack, Typography } from '@mui/material';
import { EntityList, EntityListItem, EntityListItemCell, IEntityListProps } from '@components/common';
import VisibilityIcon from '@mui/icons-material/Visibility';

type EntityType = RoleWithAssignees;

export interface IRoleListProps extends IEntityListProps<EntityType> {
	paddingX?: number | string;
	entities: EntityType[];
	onEdit?: (entity: EntityType) => void;
	onView?: (entity: EntityType) => void;
	onDelete?: (entity: EntityType) => void;
	onShowAssignees?: (entity: EntityType) => void;
}

export const RoleList: FC<IRoleListProps> = ({ entities, onEdit, onView, onDelete, onShowAssignees, paddingX, ...props }) => {
	const user = useLoggedinUser();
	const hasWritePermission = user?.hasPermission(Permission.ROLE_WRITE);
	return (
		<EntityList entities={entities} hideSelectAllButton paddingX={paddingX} {...props}>
			{entities.map((entity, i) => (
				<EntityListItem
					key={`entity-${entity.name}-${entity.id}-${i}`}
					sx={{ paddingRight: paddingX }}
					notSelectable
					actions={[
						{ text: <Tr.Portal path="view" />, onClick: () => onView?.(entity) },
						{
							text: <Tr.Portal path="edit" />,
							onClick: () => onEdit?.(entity),
							hidden: entity.isBuiltin || !hasWritePermission
						},
						{
							text: <Tr.Portal path="delete" />,
							onClick: () => onDelete?.(entity),
							hidden: entity.isBuiltin || !hasWritePermission
						}
					]}
				>
					<Grid container columnSpacing={2}>
						<Grid item xs={4}>
							<EntityListItemCell title={<Tr.Portal path="roles.role-name" />}>
								<Typography>{entity.name}</Typography>
							</EntityListItemCell>
						</Grid>
						<Grid item xs={4}>
							<EntityListItemCell title={<Tr.Portal path="roles.type" />}>
								<Typography>
									<Tr.Portal path={entity.isBuiltin ? 'roles.tab-default' : 'roles.tab-custom'} />
								</Typography>
							</EntityListItemCell>
						</Grid>
						<Grid item xs={4}>
							<EntityListItemCell
								title={<Tr.Portal path="roles.number-of-assignees" />}
								spacing={entity.assignees.length ? 0 : 1}
							>
								<Stack direction="row" alignItems="center" spacing={0.5}>
									<Typography>{NumberUtils.formatAsNumber(entity.assignees.length, { decimalScale: 0 })}</Typography>
									{entity.assignees.length ? (
										<IconButton
											sx={{ color: 'common.black', '&:hover': { color: 'primary.main' } }}
											onClick={() => onShowAssignees?.(entity)}
										>
											<VisibilityIcon fontSize="small" color="inherit" />
										</IconButton>
									) : null}
								</Stack>
							</EntityListItemCell>
						</Grid>
					</Grid>
				</EntityListItem>
			))}
		</EntityList>
	);
};
